import {Color} from '@benchling/nucleus-ui';
import {ActionIcon, createStyles, useMantineTheme} from '@mantine/core';
import React from 'react';
import {type Edge, EdgeLabelRenderer, type EdgeProps, MarkerType} from 'reactflow';

import {assert} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import BaseEdgeRenderer from 'ui-library/reactflow/edges/BaseEdgeRenderer';
import {EdgeType, LineStyle} from 'ui-library/reactflow/edges/edgeOptions';
import {getEdgeMidpointLabelCoords} from 'ui-library/reactflow/edges/ElkSplineEdgeRenderer';

import {useProcedureRunDesignFlowchartEdgeActions} from './procedureRunDesignFlowchartEdgeActionsContext';
import {useProcedureRunDesignFlowchartIsEditable} from './procedureRunDesignFlowchartEditableContext';
import type {FlowchartEdgeSpec} from './ProcedureRunDesignFlowchartLayoutUtils';
import {getProcedureRunDesignFlowchartNodeHandleId} from './ProcedureRunDesignFlowchartNodeHandle';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE = 'procedureRunDesignFlowchartEdge';

/**
 * Visual variant for a design flowchart edge. Add a variant plus a color entry to introduce new tones.
 * `readonly` is the lighter tone for edges whose source is at or after the active unit-op layer.
 * `preview` is the dashed blue tone for add-replicate preview edges.
 */
export type ProcedureRunDesignFlowchartEdgeVariant = 'editable' | 'readonly' | 'preview';

export interface ProcedureRunDesignFlowchartEdgeData {
  variant: ProcedureRunDesignFlowchartEdgeVariant;
  /**
   * Whether this edge is in the upstream-to-active position and can be removed while editing.
   * Mirrors the drawing affordance so draw and delete stay in sync. Defaults to false.
   */
  isRemovable?: boolean;
}

/** Maps an edge variant to its stroke color. */
const EDGE_VARIANT_COLORS: Record<ProcedureRunDesignFlowchartEdgeVariant, string> = {
  editable: Color.GRAY_600,
  readonly: Color.GRAY_400,
  preview: Color.BLUE_200,
};

/** Only preview edges are dashed. */
function getProcedureRunDesignFlowchartEdgeLineStyle(
  variant: ProcedureRunDesignFlowchartEdgeVariant
): LineStyle {
  return variant === 'preview' ? LineStyle.DASHED : LineStyle.SOLID;
}

/** Stroke/marker color for a variant. ReactFlow markers are per-edge, so arrowheads match the line. */
export function getProcedureRunDesignFlowchartEdgeColor(
  variant: ProcedureRunDesignFlowchartEdgeVariant
): string {
  return EDGE_VARIANT_COLORS[variant];
}

const PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_ID_SEPARATOR = '->';

export function getProcedureRunDesignFlowchartEdgeId(sourceNodeId: string, targetNodeId: string): string {
  return `${sourceNodeId}${PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_ID_SEPARATOR}${targetNodeId}`;
}

/**
 * Inverse of `getProcedureRunDesignFlowchartEdgeId`. Node ids never contain the separator, so the id
 * splits cleanly into its source and target node ids.
 */
export function parseProcedureRunDesignFlowchartEdgeId(edgeId: string): {
  sourceNodeId: string;
  targetNodeId: string;
} {
  const separatorIndex = edgeId.indexOf(PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_ID_SEPARATOR);
  assert(separatorIndex !== -1, `Malformed flowchart edge id: ${edgeId}`);
  return {
    sourceNodeId: edgeId.slice(0, separatorIndex),
    targetNodeId: edgeId.slice(separatorIndex + PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_ID_SEPARATOR.length),
  };
}

/** Options controlling edge removability. */
export interface ProcedureRunDesignFlowchartEdgeFlags {
  /** Whether the edge is in the upstream-to-active position and removable while editing. */
  isRemovable?: boolean;
}

export function makeProcedureRunDesignFlowchartEdge(
  spec: FlowchartEdgeSpec,
  {isRemovable = false}: ProcedureRunDesignFlowchartEdgeFlags = {}
): Edge<ProcedureRunDesignFlowchartEdgeData> {
  const variant = spec.variant ?? 'editable';
  return {
    id: getProcedureRunDesignFlowchartEdgeId(spec.sourceNodeId, spec.targetNodeId),
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE,
    source: spec.sourceNodeId,
    target: spec.targetNodeId,
    sourceHandle: getProcedureRunDesignFlowchartNodeHandleId(spec.sourceNodeId, 'source'),
    targetHandle: getProcedureRunDesignFlowchartNodeHandleId(spec.targetNodeId, 'target'),
    data: {variant, isRemovable},
    markerEnd: {type: MarkerType.Arrow, color: getProcedureRunDesignFlowchartEdgeColor(variant)},
  };
}

/**
 * Renders a design flowchart edge as a bezier curve colored by its variant. Removable edges in
 * edit mode tint blue on hover and show a midpoint trash button when selected. All other edges
 * render inert.
 */
export default function ProcedureRunDesignFlowchartEdge(
  props: EdgeProps<ProcedureRunDesignFlowchartEdgeData>
): JSX.Element {
  const theme = useMantineTheme();
  const isEditable = useProcedureRunDesignFlowchartIsEditable();
  const variant = props.data?.variant ?? 'editable';
  const canEdit = isEditable && (props.data?.isRemovable ?? false);
  const isSelected = canEdit && (props.selected ?? false);
  return (
    <>
      <BaseEdgeRenderer
        {...props}
        selected={isSelected}
        data={{
          type: EdgeType.BEZIER,
          color: getProcedureRunDesignFlowchartEdgeColor(variant),
          lineStyle: getProcedureRunDesignFlowchartEdgeLineStyle(variant),
          hoverColor: canEdit ? theme.colors.blue[4] : undefined,
        }}
      />
      {isSelected && <ProcedureRunDesignRemoveEdgeLabel {...props} />}
    </>
  );
}

/**
 * Midpoint trash button for a selected removable edge, rendered via EdgeLabelRenderer.
 * Reads onRemoveEdge from context so the handler stays stable.
 */
function ProcedureRunDesignRemoveEdgeLabel(props: EdgeProps): JSX.Element {
  const {classes, cx} = useStyles();
  const {onRemoveEdge} = useProcedureRunDesignFlowchartEdgeActions();
  const {labelX, labelY} = getEdgeMidpointLabelCoords(props);
  return (
    <EdgeLabelRenderer>
      <div
        // Only the transform is dynamic (depends on the edge midpoint); static styles live in classes.
        style={{transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`}}
        className={cx(classes.labelContainer, 'nopan')}
      >
        <ActionIcon
          variant="tertiary"
          size="sm"
          onClick={() => {
            onRemoveEdge(props.id);
          }}
          aria-label="Remove edge"
          {...getTestProps({component: 'ProcedureRunDesignFlowchartEdge', element: 'removeButton'})}
        >
          <BenchlingIcon icon="trash-can" />
        </ActionIcon>
      </div>
    </EdgeLabelRenderer>
  );
}

const useStyles = createStyles(() => ({
  labelContainer: {
    position: 'absolute',
    pointerEvents: 'all',
  },
}));

export const EDGE_TYPES = {
  [PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE]: ProcedureRunDesignFlowchartEdge,
};
